import React from 'react';
import { Theme, Typography, Button } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';

import { withTranslation, WithTranslation } from 'react-i18next';
import banknoteJammed from 'assets/images/banknote-jammed.svg';
import outOfPaper from 'assets/images/out-of-paper.svg';
import classNames from 'classnames';
import { observer, inject } from 'mobx-react';

import StoreModel from 'models/Index';
import { Services } from 'helpers/constants/Services';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { RoutePaths } from 'helpers/constants/RoutePaths';
import { AlertTriangleIcon } from 'assets/images/AlertTriangleIcon';

interface KioskOutOfOrderProps
    extends RouteComponentProps,
        WithStyles<typeof styles>,
        WithTranslation {}
interface KioskOutOfOrderPropsInjected extends KioskOutOfOrderProps {
    Store: StoreModel;
}

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            margin: 'auto',
        },
        logo: {
            height: '10vh',
        },
        header: {
            color: '#2677BD',
            textAlign: 'center',
            margin: '4vh 0',
            fontSize: '4rem',
        },
        statusSection: {
            marginTop: '100px',
            marginBottom: '128px',
            maxWidth: '60%',
            '& >div': {
                display: 'flex',
                margin: '50px',
            },
        },
        kioskId: {
            color: '#303130',
            textAlign: 'center',
            fontSize: '2rem',
            fontFamily: 'Montserrat',
        },
        statusLogo: {
            height: '3vh',
            marginRight: '50px',
        },
        statusValue: {
            marginTop: '10px',
            fontSize: '1.6rem',
            fontFamily: 'Montserrat',
        },
        red: {
            color: '#FF0101',
        },
        button: {
            width: '480px',
            height: '104px',
            marginBottom: '32px',
        },
    });

@inject(Services.STORE)
@observer
class KioskOutOfOrder extends React.Component<KioskOutOfOrderProps, any> {
    checkTimerId?: any;
    checkTimerInterval: number = 2 * 60 * 1000;
    checkStatusEventHandler: any = this.checkStatus.bind(this);

    get injected() {
        return this.props as KioskOutOfOrderPropsInjected;
    }

    componentDidMount() {
        const { Store } = this.injected;
        window.addEventListener('online', this.checkStatusEventHandler);
        Store.reset();

        this.startCheckTimer();
    }

    componentWillUnmount() {
        window.removeEventListener('online', this.checkStatusEventHandler);
        this.stopCheckTimer();
    }

    render() {
        const { classes, t, history, location } = this.props;
        const { Store } = this.injected;
        const { peripheryStatus, kioskReadyStatus } = Store;

        return (
            <div className={classes.root}>
                <AlertTriangleIcon className={classes.logo} />
                <Typography className={classes.header} variant='h3'>
                    {t('Kiosk is out of order')}
                </Typography>
                <Typography className={classes.kioskId} variant='h5'>
                    {t('Kiosk id')}: {Store.kioskName}
                </Typography>
                <div className={classes.statusSection}>
                    {!peripheryStatus.isPrinterReady && (
                        <div>
                            <img
                                src={outOfPaper}
                                alt={'Logo'}
                                className={classes.statusLogo}
                            />
                            <Typography
                                variant='h5'
                                className={classNames(
                                    classes.statusValue,
                                    classes.red
                                )}
                            >
                                {t(peripheryStatus.printerError)}
                            </Typography>
                        </div>
                    )}
                    {!peripheryStatus.isCashAccepterReady && (
                        <div>
                            <img
                                src={banknoteJammed}
                                alt={'Logo'}
                                className={classes.statusLogo}
                            />
                            <Typography
                                variant='h5'
                                className={classNames(
                                    classes.statusValue,
                                    classes.red
                                )}
                            >
                                {t(peripheryStatus.cashAccepterError)}
                            </Typography>
                        </div>
                    )}
                    {!peripheryStatus.isKioskOnline && (
                        <div>
                            <Typography
                                variant='h5'
                                className={classNames(
                                    classes.statusValue,
                                    classes.red
                                )}
                            >
                                {t('There is no Internet connection')}
                            </Typography>
                        </div>
                    )}
                    {!kioskReadyStatus.isBatchOpen && (
                        <div>
                            <Typography
                                variant='h5'
                                className={classNames(
                                    classes.statusValue,
                                    classes.red
                                )}
                            >
                                {t('Batch is closed')}
                            </Typography>
                        </div>
                    )}
                    {!Store.isRegisterEnabled && (
                        <div>
                            <Typography
                                variant='h5'
                                className={classNames(
                                    classes.statusValue,
                                    classes.red
                                )}
                            >
                                {t('Kiosk register is disabled')}
                            </Typography>
                        </div>
                    )}
                    {!Store.isKioskRegistered && (
                        <div>
                            <Typography
                                variant='h5'
                                className={classNames(
                                    classes.statusValue,
                                    classes.red
                                )}
                            >
                                {t('Kiosk is not registered')}
                            </Typography>
                        </div>
                    )}
                </div>
                <Button
                    className={classes.button}
                    variant='primary'
                    onClick={(_) => this.checkStatus()}
                >
                    <Typography variant='button' style={{ fontSize: '1.9rem' }}>
                        {t('Retry')}
                    </Typography>
                </Button>

                {peripheryStatus.hasErrors &&
                    Store.canProceedWithoutPrinter && (
                        <Button
                            className={classes.button}
                            variant='secondary'
                            onClick={(_) => {
                                Store.setValue({
                                    isProceedWithoutPrinter: true,
                                });
                                history.push({
                                    ...location,
                                    pathname: RoutePaths.LanguageSelect,
                                });
                            }}
                        >
                            <Typography
                                variant='button'
                                style={{ fontSize: '1.9rem' }}
                            >
                                {t('Proceed without receipt')}
                            </Typography>
                        </Button>
                    )}
            </div>
        );
    }

    startCheckTimer() {
        this.checkTimerId = setInterval(
            () => this.checkStatus(),
            this.checkTimerInterval
        );
    }

    stopCheckTimer() {
        if (this.checkTimerId) {
            clearInterval(this.checkTimerId);
        }
    }

    async checkStatus() {
        const { history, location } = this.props;
        const { Store } = this.injected;
        const { kioskReadyStatus, peripheryStatus } = Store;

        const promises = [];

        if (!kioskReadyStatus.isBatchOpen) {
            promises.push(kioskReadyStatus.checkBatch());
        }

        if (peripheryStatus.hasErrors) {
            promises.push(peripheryStatus.check());
        }

        if (!Store.isRegisterEnabled) {
            promises.push(Promise.resolve(Store.isRegisterEnabled));
        }

        if (!Store.registerName.length) {
            promises.push(Promise.resolve(Store.registerName.length !== 0));
        }

        if (
            !Store.deviceProfile.enableDeployment ||
            !Store.deviceProfile.applications.length
        ) {
            promises.push(
                Store.loadDeviceProfile()
                    .then(() => Store.deviceProfile.enableDeployment)
                    .then(
                        (enableDeployment) =>
                            enableDeployment &&
                            Store.deviceProfile.applications.length > 0
                    )
            );
        }

        await Promise.all(promises).then((values) => {
            if (values.every((isReady) => isReady === true)) {
                history.push({ pathname: '/', search: location.search });
            }
        });
    }
}

export default withTranslation()(
    withStyles(styles)(withRouter(KioskOutOfOrder))
);
